import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { PositionGroupModel, ResponseModel } from '../models';
import { DisplayToaster } from '../decorators/toaster';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { environment } from '../../../environments/environment';
import { selectCompanyProfile } from '../../administration/store/selectors';
import { CompanyProfile } from '../../administration/models/company-profile.model';
import { AppState } from '../../store/state/app.state';
import { LoadOutgoingInvoicePositionsGroups } from '../../outgoing-invoice/store/actions/outgoing-invoice.actions';
import { ProductTypes } from '../../../../../../common/src/modules/products/product-types';

@Injectable()
export class PositionGroupsService {
  public wid: number;
  private readonly apiEndpoint: string = '/position/groups';
  private readonly oinApiEndpoint: string = `${environment.javaApiVersion}/outgoing-invoices`;

  private apiUrl(url: string = ''): string {
    return this.apiEndpoint + url;
  }

  constructor(
    private readonly store: Store<AppState>,
    private readonly toasterService: ToasterService,
    private readonly http: HttpClient,
  ) {
    this.store.select(selectCompanyProfile)
      .subscribe((profile: CompanyProfile) => {
        this.wid = profile.workspaceId;
      });
  }

  @DisplayToaster({showErrorMessage: true})
  public createGroup(
    name: string,
    // documentType: DocumentTypesUppercaseEnum,
    documentType: string,
    documentId: number,
  ): Observable<PositionGroupModel> {
    return this.http.post(this.apiUrl(), { name, documentType, documentId })
      .pipe(map((response: ResponseModel<PositionGroupModel>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public createOINGroup(
    name: string,
    oinId: number,
  ): Observable<PositionGroupModel[]> {
    const params = {
      wid: this.wid.toString()
    };
    return this.http.post(`${this.oinApiEndpoint}/${oinId}/position-groups`, { name }, { params })
      .pipe(
        map((response: ResponseModel<PositionGroupModel[]>) => this.setOinGroupsNames(response.data)),
        tap((groups: PositionGroupModel[]) => {
          this.store.dispatch(LoadOutgoingInvoicePositionsGroups({ positionsGroups: groups }));
        }),
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public getOINGroups(oinId: number,): Observable<PositionGroupModel[]> {
    const params = {
      wid: this.wid.toString()
    };
    return this.http.get(`${this.oinApiEndpoint}/${oinId}/position-groups`, { params })
      .pipe(
        map((response: ResponseModel<PositionGroupModel[]>) => this.setOinGroupsNames(response.data)),
        tap((groups: PositionGroupModel[]) => {
          this.store.dispatch(LoadOutgoingInvoicePositionsGroups({ positionsGroups: groups }));
        }),
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public deleteGroup(groupId: number|string): Observable<null> {
    return this.http.delete(this.apiUrl(`/${groupId}`))
      .pipe(map((response: ResponseModel<null>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public deleteOINGroup(oinId: number|string, groupId: number|string): Observable<null> {
    return this.http.delete(`${this.oinApiEndpoint}/${oinId}/position-groups/${groupId}`)
      .pipe(map((response: ResponseModel<null>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public updateGroup(
    groupId: number|string,
    body: {
      name?: string;
      orderNumber?: number;
    }
  ): Observable<PositionGroupModel> {
    return this.http.patch(this.apiUrl(`/${groupId}`), body)
      .pipe(map((response: ResponseModel<PositionGroupModel>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public updateOINGroup(
    oinId: number|string,
    groupId: number|string,
    body: {
      fieldName: string,
      fieldValue: any
    }
  ): Observable<PositionGroupModel> {
    return this.http.patch(`${this.oinApiEndpoint}/${oinId}/position-groups/${groupId}`, body)
      .pipe(map((response: ResponseModel<PositionGroupModel>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public moveOINPositionToGroup(
    oinId: number|string,
    groupId: number|string,
    body: {
      ids: number[]
    }
  ): Observable<PositionGroupModel[]> {
    return this.http.patch(`${this.oinApiEndpoint}/${oinId}/position-groups/${groupId}/add`, body)
      .pipe(
        map((response: ResponseModel<PositionGroupModel[]>) => this.setOinGroupsNames(response.data)),
        tap((groups: PositionGroupModel[]) => {
          this.store.dispatch(LoadOutgoingInvoicePositionsGroups({ positionsGroups: groups }));
        }),
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public getGroupsByDocument(documentId: number, documentType: string): Observable<PositionGroupModel[]> {
    return this.http.get(this.apiUrl(`/${documentType}/${documentId}`))
      .pipe(map((response: ResponseModel<PositionGroupModel[]>) => response.data));
  }

  public setOinGroupsNames(groups: PositionGroupModel[]): PositionGroupModel[] {
    return groups.map((g: PositionGroupModel) => ({
      ...g,
      name: !g.isDefault && g.system
        ? g.name.toLowerCase()
          .replace(ProductTypes.CORRECTION, 'FORM.CORRECTIONS')
          .replace(ProductTypes.OVERDUE_FEE, 'OVERDUE_NOTICE.OVERDUE_FEES_TABLE')
          .replace(ProductTypes.PREPAYMENT, 'FINAL_INVOICE.DOWN_PAYMENTS')
          .replace(ProductTypes.INVOICE, 'APP.INVOICES')
          .replace(ProductTypes.CREDIT_NOTE, 'APP.CREDIT_NOTES')
          .replace(ProductTypes.CORRECTION, 'FORM.CORRECTIONS')
          .replace(ProductTypes.OVERDUE_FEE, 'OVERDUE_NOTICE.OVERDUE_FEES_TABLE')
          .replace(ProductTypes.PREPAYMENT, 'FINAL_INVOICE.DOWN_PAYMENTS')
          .replace(ProductTypes.INVOICE, 'APP.INVOICES')
          .replace(ProductTypes.CREDIT_NOTE, 'APP.CREDIT_NOTES')
        : g.name
    }));
  }

}
